/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { NavLink, useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Link from "@mui/material/Link";
import List from "@mui/material/List";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI example components
import SidenavFooter from "examples/Sidenav/SidenavFooter";
import SidenavItem from "examples/Sidenav/SidenavItem";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Argon Dashboard 2 MUI context
import { setMiniSidenav, useArgonController } from "context";
import { useUserData } from "context/UserDataContext";
import { useLanguage } from "context/LanguageContext";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useArgonController();
  const { miniSidenav, darkSidenav, layout } = controller;
  const location = useLocation();
  const { pathname } = location;
  const rawItemName = pathname.split("/").slice(1)[0];
  // Cuando estamos en historial-acciones (accedido desde ajustes), mantenemos ajustes activo
  const itemName = rawItemName === 'historial-acciones' ? 'ajustes' : rawItemName;
  const token = localStorage.getItem("empresasJWT");
  const { empresaData } = useUserData();
  const { t } = useLanguage();

  const logoDefault = "https://staging.payflexapp.com/static/media/logo-black.408dd4f72514ec12bf45.png";
  const [logoSrc, setLogoSrc] = useState(logoDefault);

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

  

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  useEffect(() => {
    let cancelled = false;

    const preloadLogo = (url) =>
      new Promise((resolve, reject) => {
        if (!url || typeof url !== "string" || url.trim() === "") {
          reject(new Error("Logo URL inválida"));
          return;
        }

        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = () => reject(new Error("Error al precargar logo"));
        img.src = url;
      });

    const applyLogo = async (url) => {
      if (!url || typeof url !== "string" || url.trim() === "") return;

      try {
        const loadedUrl = await preloadLogo(url);
        if (!cancelled) {
          setLogoSrc(loadedUrl);
        }
      } catch (_) {
        if (!cancelled) {
          setLogoSrc(logoDefault);
        }
      }
    };

    if (empresaData?.logo && typeof empresaData.logo === "string" && empresaData.logo.trim() !== "") {
      applyLogo(empresaData.logo);
      return () => {
        cancelled = true;
      };
    }

    try {
      const storedEmpresaData = localStorage.getItem("empresaData");
      if (storedEmpresaData) {
        const parsedEmpresaData = JSON.parse(storedEmpresaData);
        if (
          parsedEmpresaData?.logo &&
          typeof parsedEmpresaData.logo === "string" &&
          parsedEmpresaData.logo.trim() !== ""
        ) {
          applyLogo(parsedEmpresaData.logo);
          return () => {
            cancelled = true;
          };
        }
      }
    } catch (_) {
      // Ignorar JSON inválido y continuar con fallback.
    }

    const empresaId = empresaData?.id;
    const jwt = localStorage.getItem("empresasJWT");

    if (!empresaId || !jwt) {
      setLogoSrc(logoDefault);
      return () => {
        cancelled = true;
      };
    }

    fetch(`${process.env.REACT_APP_API_URL}/datos-empresa/${empresaId}`, {
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data && typeof data.logo === "string" && data.logo.trim() !== "") {
          applyLogo(data.logo);
        } else {
          setLogoSrc(logoDefault);
        }
      })
      .catch(() => {
        setLogoSrc(logoDefault);
      });

    return () => {
      cancelled = true;
    };
  }, [empresaData, token]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  
  const filteredRoutes = routes.filter(route => route.includeInSidebar);
  const renderRoutes = filteredRoutes.map(({ type, name, nameKey, icon, title, titleKey, key, href, route }) => {
    let returnValue;

    const displayName = nameKey ? t(nameKey) : name;
    const displayTitle = titleKey ? t(titleKey) : title;

    if (type === "route") {
      if (href) {
        returnValue = (
          <Link href={href} key={key} target="_blank" rel="noreferrer">
            <SidenavItem
              name={displayName}
              icon={icon}
              active={key === itemName}
            />
          </Link>
        );
      } else {
        returnValue = (
          <NavLink to={route} key={key}>
            <SidenavItem name={displayName} icon={icon} active={key === itemName} />
          </NavLink>
        );
      }
    } else if (type === "title") {
      returnValue = (
        <ArgonTypography
          key={key}
          color={darkSidenav ? "white" : "dark"}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          opacity={0.6}
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {displayTitle}
        </ArgonTypography>
      );
    } else if (type === "divider") {
      returnValue = <Divider key={key} light={darkSidenav} />;
    }

    return returnValue;
  });

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ darkSidenav, miniSidenav, layout }}>
      <ArgonBox pt={1} pb={1} px={4} textAlign="center">
        <ArgonBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <ArgonTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </ArgonTypography>
        </ArgonBox>
        <ArgonBox
          component={NavLink}
          to="/"
          display="flex"
          alignItems="left"
          justifyContent="left"
          sx={{ minHeight: "46px", mb: -1.5 }}
        >
          <ArgonBox position="relative" width="8.2rem" height="3.2rem" sx={{ flexShrink: 0 }}>
            <ArgonBox
              component="img"
              src={logoSrc}
              alt="Logo Empresa"
              width="8.2rem"
              height="3.2rem"
              onError={() => {
                if (logoSrc !== logoDefault) {
                  setLogoSrc(logoDefault);
                }
              }}
              sx={{
                position: "relative",
                zIndex: 1,
                objectFit: "contain",
                display: "block",
              }}
            />
          </ArgonBox>
        </ArgonBox>
      </ArgonBox>
      <Divider light={darkSidenav} />
      <List>{renderRoutes}</List>

      <ArgonBox pt={1} mt="auto" mb={2} mx={2}>
        <SidenavFooter />
      </ArgonBox>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;

